import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import {
  type LegacyRef,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
  type MouseEvent as ReactMouseEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  ReactPortal,
} from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import type { Modifier } from 'react-popper';
import { usePopper } from 'react-popper';
import type {
  ActionMeta,
  GroupBase,
  MultiValue,
  OnChangeValue,
  Options,
  MenuProps as ReactSelectMenuProps,
} from 'react-select';

import { Button } from '@dynatrace/strato-components/buttons';
import { ProgressCircle } from '@dynatrace/strato-components/content';
import {
  Flex,
  Divider,
  Surface,
  _surfaceBorderRadius as surfaceBorderRadius,
} from '@dynatrace/strato-components/layouts';
import { Text } from '@dynatrace/strato-components/typography';
import Borders from '@dynatrace/strato-design-tokens/borders';
import Colors from '@dynatrace/strato-design-tokens/colors';
import {
  CheckmarkSmallIcon,
  MagnifyingGlassIcon,
  MinusIcon,
} from '@dynatrace/strato-icons';

import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { useOverlayStacking } from '../../../core/hooks/useOverlayStacking.js';
import { OverlayStackingProvider } from '../../../core/providers/OverlayStackingProvider.js';
import { useOverlay } from '../../../overlays/hooks/useOverlay.js';
import { observeOverlaySize } from '../../../overlays/utils/popper-modifiers.js';
import { _FormFieldContext as FormFieldContext } from '../../forms-core/contexts/FormFieldContext.js';
import { FormFieldMessageContext } from '../../forms-core/contexts/FormFieldMessageContext.js';
import { TextInput } from '../../text-input/TextInput.js';
import { useSelectMenuContext } from '../hooks/use-select-menu-context.js';
import { SelectActions } from '../state/select-menu-state-reducer.js';
import {
  selectMenuDividerCSS,
  selectMenuOverlayCSS,
  selectOptionMultiCSS,
  selectOptionMultiIndicatorCSS,
  selectOptionMultiIndicatorIconCSS,
  selectAllCSS,
} from '../styles/Select.sty.js';

type MenuProps<
  OptionValue,
  isMulti extends boolean,
  Group extends GroupBase<OptionValue>,
> = ReactSelectMenuProps<OptionValue, isMulti, Group>;

// Popper modifier to sync the width of the overlay menu
const sameWidthModifier: Modifier<'sameWidth-as-trigger'> = {
  name: 'sameWidth-as-trigger',
  enabled: true,
  fn: ({ state }) => {
    state.styles.popper.width = `${state.rects.reference.width}px`;
    state.styles.popper.height = 'min-content';
  },
  phase: 'beforeWrite',
  requires: ['computeStyles'],
};

const heightModifier: Modifier<'selectv2-height-handler'> = {
  name: 'selectv2-height-handler',
  enabled: true,
  fn: ({ state }): void => {
    const viewportHeight = Math.max(
      document.documentElement.clientHeight || 0,
      window.innerHeight || 0,
    );

    const topOffset = state.rects.reference.y;
    const bottomOffset =
      viewportHeight - (state.rects.reference.y + state.rects.reference.height);
    const offset = state.options.placement.includes('bottom')
      ? bottomOffset
      : topOffset;

    if (offset < state.rects.popper.height) {
      // Limit the height to half the viewport height or 320px (which ever is greater),
      // subtract the height of the trigger and remove a 20 px buffer to the screen edge.
      const maxHeightBase = viewportHeight / 2 > 320 ? viewportHeight / 2 : 320;
      state.styles.popper.maxHeight = `${
        maxHeightBase - state.rects.reference.height - 20
      }px`;
    }
  },
  phase: 'beforeWrite',
  requires: ['computeStyles'],
};

export function Menu<
  OptionValue,
  isMulti extends boolean,
  Group extends GroupBase<OptionValue>,
>(props: MenuProps<OptionValue, isMulti, Group>): ReactPortal {
  const { children, innerRef, innerProps, selectProps } = props;
  const {
    filterRef,
    clearSelectionRef,
    triggerRef,
    inputValue,
    isMulti,
    onInputChange,
  } = selectProps;
  const {
    className: contentConsumerClassName,
    style: contentConsumerStyle,
    'data-testid': contentDataTestId = 'selectv2-menu',
  } = selectProps.componentProps.content ?? {};
  const {
    className: filterConsumerClassName,
    style: filterConsumerStyle,
    'data-testid': filterDataTestId,
  } = selectProps.componentProps.filter ?? {};

  const [portalRef, setPortalRef] = useState<HTMLDivElement | null>(null);

  const rootPortalRef = useMergeRefs([innerRef, setPortalRef]);

  const stackingContext = useOverlayStacking(undefined, 'overlay');

  const { styles: positioningStyles } = usePopper(triggerRef, portalRef, {
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 4],
        },
      },
      sameWidthModifier,
      heightModifier,
      // TODO: Deprecation is only happening to the public export, the internal function will still be available
      // can be removed with APPDEV-12712
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      observeOverlaySize,
    ],
  });

  const { dispatch } = useSelectMenuContext();

  const clearFilterValue = useCallback(() => {
    if (inputValue !== '') {
      onInputChange('', {
        action: 'input-change',
        prevInputValue: inputValue,
      });
    }
  }, [inputValue, onInputChange]);

  useOverlay(
    {
      onClose: () => {
        dispatch({
          type: SelectActions.CLOSE,
        });
        clearFilterValue();
      },
      isOpen: selectProps.menuIsOpen,
      isDismissible: true,
      shouldCloseOnInteractOutside: (element) => {
        // close the menu when the click is outside of the select and the select trigger.
        // The closing when the trigger is clicked is handled by the trigger itself
        return !selectProps.triggerRef?.contains(element);
      },
    },
    innerRef as RefObject<HTMLElement>,
  );

  useEffect(() => {
    if (selectProps.menuIsOpen) {
      filterRef?.current?.inputRef?.focus({ preventScroll: true });
    }
  }, [filterRef, selectProps.menuIsOpen]);

  const hiddenTrigger = useMemo(
    () => selectProps.triggerRef?.querySelector('input'),
    [selectProps.triggerRef],
  );

  useEffect(() => {
    // we need to manually restore the focus to the trigger when the menu closes now, because we prevented the react-select default behavior
    return () => hiddenTrigger?.focus();
  }, [hiddenTrigger]);

  const intl = useIntl();

  const ariaLabelId = useId();

  const allSelected = useMemo(() => {
    return (
      isMulti &&
      selectProps.allOptions?.length ===
        (selectProps.value as MultiValue<OptionValue>)?.length
    );
  }, [isMulti, selectProps.allOptions, selectProps.value]);

  const handleSelectAllInteraction = () => {
    if (!selectProps.loading) {
      if (allSelected) {
        selectProps.onChange([] as OnChangeValue<OptionValue, isMulti>, {
          action: 'clear',
          removedValues: selectProps.value as Options<OptionValue>,
        });
      } else {
        selectProps.onChange(
          selectProps.allOptions as OnChangeValue<OptionValue, isMulti>,
          {} as ActionMeta<OptionValue>,
        );
      }
    }
  };

  const noOptionsAvailable =
    selectProps.allOptions === undefined || selectProps.allOptions.length === 0;
  const isFiltering =
    selectProps.customFilter && inputValue !== undefined && inputValue !== '';
  const showClearSelection =
    selectProps.clearable &&
    selectProps.value &&
    isMulti === false &&
    !isFiltering;
  const showSelectAll =
    selectProps.clearable && isMulti && !isFiltering && !noOptionsAvailable;

  return createPortal(
    <Surface
      data-testid={contentDataTestId}
      ref={rootPortalRef}
      {...(innerProps as unknown as Record<string, unknown>)}
      elevation="floating"
      padding={4}
      className={clsx(
        selectMenuOverlayCSS({
          isTriggerWidth: selectProps.contentWidth === 'trigger',
        }),
        contentConsumerClassName,
      )}
      style={{
        zIndex: stackingContext.style.zIndex,
        ...positioningStyles.popper,
        width:
          selectProps.contentWidth !== 'trigger'
            ? selectProps.contentWidth
            : positioningStyles.popper.width,
        ...contentConsumerStyle,
        ...assignInlineVars({
          [surfaceBorderRadius]: Borders.Radius.Surface.Subdued,
        }),
      }}
      onMouseDown={(e: ReactMouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <OverlayStackingProvider>
        {selectProps.customFilter && (
          // The empty content is need so the TextInput does not consume the required coming from the FormField
          <FormFieldContext.Provider value={{}}>
            {/* The TextInput should not consume any validation context to not reflect error states. */}
            <FormFieldMessageContext.Provider
              value={{
                state: {
                  customMessages: [],
                  formControlMessages: [],
                  showError: false,
                },
              }}
            >
              <TextInput
                data-testid={filterDataTestId}
                ref={filterRef}
                variant="minimal"
                placeholder={intl.formatMessage({
                  defaultMessage: 'Search',
                  id: 'CerADEHqR/6IVmHr',
                  description: 'Placeholder for the filter inside the select',
                })}
                value={inputValue}
                onChange={(value: string) => {
                  onInputChange(value, {
                    action: 'input-change',
                    prevInputValue: inputValue,
                  });
                }}
                className={filterConsumerClassName}
                style={filterConsumerStyle}
              >
                <TextInput.Suffix>
                  <MagnifyingGlassIcon
                    style={{ fill: Colors.Icon.Neutral.Subdued }}
                  />
                </TextInput.Suffix>
              </TextInput>
            </FormFieldMessageContext.Provider>
            <Divider
              className={clsx(selectMenuDividerCSS, 'strato-selectv2-divider')}
            />
          </FormFieldContext.Provider>
        )}
        {showClearSelection && (
          <>
            <Button
              ref={clearSelectionRef as LegacyRef<HTMLButtonElement>}
              width="full"
              textAlign="start"
              onClick={() => {
                selectProps.onChange(
                  null as OnChangeValue<OptionValue, isMulti>,
                  {
                    action: 'clear',
                    removedValues: selectProps.value as Options<OptionValue>,
                  },
                );
                // close the menu in single selection mode and clear the filter
                if (isMulti === false) {
                  dispatch({
                    type: SelectActions.CLOSE,
                  });
                  clearFilterValue();
                }
              }}
              onKeyDown={(event: ReactKeyboardEvent<HTMLButtonElement>) => {
                if (event.key === ' ' || event.key === 'Enter') {
                  selectProps.onChange(
                    null as OnChangeValue<OptionValue, isMulti>,
                    {
                      action: 'clear',
                      removedValues: selectProps.value as Options<OptionValue>,
                    },
                  );
                  // prevent option selection being triggered
                  event.preventDefault();
                  event.stopPropagation();

                  // close the menu in single selection mode and clear the filter
                  if (isMulti === false) {
                    dispatch({
                      type: SelectActions.CLOSE,
                    });
                    clearFilterValue();
                  }

                  // move the focus back to the trigger after clearing if possible
                  // otherwise move it back to the input
                  if (filterRef?.current && isMulti) {
                    filterRef.current.inputRef?.focus();
                  } else {
                    const hiddentrigger = triggerRef?.querySelector('input');
                    hiddentrigger?.focus();
                  }
                }
              }}
            >
              {intl.formatMessage({
                defaultMessage: 'Clear selection',
                id: '/H3rbOvaR6bAasVo',
                description: 'Select component clear selection button.',
              })}
            </Button>
            <Divider
              className={clsx(selectMenuDividerCSS, 'strato-selectv2-divider')}
            />
          </>
        )}
        {showSelectAll && (
          <>
            <Flex
              tabIndex={0}
              role="option"
              className={clsx(
                'strato-selectV2-option',
                selectOptionMultiCSS,
                selectAllCSS,
                {
                  'strato-selectV2-option--selected':
                    (selectProps.value as MultiValue<OptionValue>)?.length > 0,
                  'strato-selectV2-option--disabled': selectProps.loading,
                },
              )}
              ref={selectProps.clearSelectionRef as LegacyRef<HTMLDivElement>}
              onClick={() => {
                handleSelectAllInteraction();
              }}
              onKeyDown={(event: ReactKeyboardEvent<HTMLDivElement>) => {
                if (event.key === ' ' || event.key === 'Enter') {
                  handleSelectAllInteraction();
                  event.preventDefault();
                  event.stopPropagation();
                }
              }}
            >
              <div
                className={clsx(
                  selectOptionMultiIndicatorCSS({
                    selected:
                      (selectProps.value as MultiValue<OptionValue>)?.length >
                      0,
                  }),
                )}
              >
                {allSelected ? (
                  <CheckmarkSmallIcon
                    className={selectOptionMultiIndicatorIconCSS}
                  />
                ) : null}
                {!allSelected &&
                (selectProps.value as MultiValue<OptionValue>)?.length > 0 ? (
                  <MinusIcon className={selectOptionMultiIndicatorIconCSS} />
                ) : null}
              </div>
              {intl.formatMessage({
                defaultMessage: 'Select all',
                id: 'EsLvnx9UfUL/bZ/r',
                description: 'Select all option in the Select component.',
              })}
            </Flex>
            <Divider
              className={clsx(selectMenuDividerCSS, 'strato-selectv2-divider')}
            />
          </>
        )}
        {selectProps.loading ? (
          <Flex
            paddingY={6}
            paddingLeft={8}
            paddingRight={12}
            style={{ color: Colors.Text.Neutral.Subdued }}
          >
            <ProgressCircle size="small" aria-labelledby={ariaLabelId} />
            <Text id={ariaLabelId}>
              <FormattedMessage
                description="Label for the loading state."
                defaultMessage="Loading"
                id="cycz+bYG+VH7Of4e"
              />
            </Text>
          </Flex>
        ) : (
          children
        )}
      </OverlayStackingProvider>
    </Surface>,
    document.body,
  );
}
